import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { AuthBrandPanel } from "@/components/AuthBrandPanel";
import { Wrench, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      <AuthBrandPanel />
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <Wrench className="w-5 h-5 text-primary-foreground" />
            </div>
            <div className="font-display font-black text-2xl uppercase">SynqMoto<span className="text-primary"> ERP</span></div>
          </div>

          {sent ? (
            <div data-testid="forgot-success">
              <MailCheck className="w-10 h-10 text-primary mb-4"/>
              <h2 className="font-display font-black text-4xl uppercase tracking-tighter mb-3">Check your inbox</h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-8">
                If <span className="text-foreground font-mono">{email}</span> is registered with us, a password reset link is on its way. The link expires in 1 hour.
              </p>
              <Link to="/login" data-testid="forgot-back-login">
                <Button className="w-full h-11 rounded-sm bg-primary hover:bg-orange-600 text-primary-foreground uppercase tracking-[0.15em] font-bold text-sm">Back to Sign In</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">Account Recovery</div>
              <h2 className="font-display font-black text-4xl uppercase tracking-tighter mb-3">Forgot password</h2>
              <p className="text-muted-foreground text-sm mb-8">Enter your registered email and we'll send you a reset link.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Registered Email</Label>
                  <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 rounded-sm bg-secondary border-border font-mono text-sm h-11" placeholder="you@shop.com" required />
                </div>
                <Button data-testid="forgot-submit" type="submit" disabled={busy}
                  className="w-full h-11 rounded-sm bg-primary hover:bg-orange-600 text-primary-foreground uppercase tracking-[0.15em] font-bold text-sm">
                  {busy ? "Sending…" : "Send Reset Link →"}
                </Button>
              </form>
              <Link to="/login" data-testid="forgot-back-link" className="mt-6 flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors">
                <ArrowLeft className="w-3 h-3"/> Back to Sign In
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
